import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Form,
    Modal,
    Pagination,
    Row,
    Spinner,
    Table,
} from "react-bootstrap";
import axios from "axios";
import Swal from "sweetalert2";
import { TranslatorContext } from "../../../../context/Translator";
import PageLayout from "../../../../layouts/PageLayout";

const PAGE_SIZE = 20;

const getAuthConfig = () => ({
    headers: {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        Accept: "application/json",
    },
});

const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? "-"
        : date.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

const formatAmount = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount)
        ? amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : "0.00";
};

const getStatusVariant = (status) => {
    switch ((status || "").toLowerCase()) {
        case "approved":
            return "success";
        case "cancelled":
            return "danger";
        default:
            return "warning";
    }
};

function PaymentPagination({ pagination, onPageChange }) {
    if (pagination.lastPage <= 1) return null;

    const pages = [];
    const firstVisible = Math.max(1, pagination.currentPage - 1);
    const lastVisible = Math.min(pagination.lastPage, pagination.currentPage + 1);

    pages.push(
        <Pagination.Prev
            key="previous"
            disabled={pagination.currentPage <= 1}
            onClick={() => onPageChange(pagination.currentPage - 1)}
        />
    );
    if (firstVisible > 1) {
        pages.push(<Pagination.Item key={1} onClick={() => onPageChange(1)}>1</Pagination.Item>);
        if (firstVisible > 2) pages.push(<Pagination.Ellipsis key="left-ellipsis" disabled />);
    }
    for (let page = firstVisible; page <= lastVisible; page += 1) {
        pages.push(
            <Pagination.Item
                key={page}
                active={page === pagination.currentPage}
                onClick={() => onPageChange(page)}
            >
                {page}
            </Pagination.Item>
        );
    }
    if (lastVisible < pagination.lastPage) {
        if (lastVisible < pagination.lastPage - 1) {
            pages.push(<Pagination.Ellipsis key="right-ellipsis" disabled />);
        }
        pages.push(
            <Pagination.Item
                key={pagination.lastPage}
                onClick={() => onPageChange(pagination.lastPage)}
            >
                {pagination.lastPage}
            </Pagination.Item>
        );
    }
    pages.push(
        <Pagination.Next
            key="next"
            disabled={pagination.currentPage >= pagination.lastPage}
            onClick={() => onPageChange(pagination.currentPage + 1)}
        />
    );

    return (
        <Card.Footer className="bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
            <small className="text-muted">
                {pagination.from || 0}-{pagination.to || 0} of {pagination.total} payments
            </small>
            <Pagination size="sm" className="mb-0">{pages}</Pagination>
        </Card.Footer>
    );
}

export default function PaymentsPage() {
    const { t } = useContext(TranslatorContext);
    const [payments, setPayments] = useState([]);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        lastPage: 1,
        total: 0,
        from: 0,
        to: 0,
    });
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("pending");
    const [typeFilter, setTypeFilter] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [loading, setLoading] = useState(true);
    const [actionId, setActionId] = useState(null);
    const [error, setError] = useState("");
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const loadPayments = useCallback(async (currentPage) => {
        setLoading(true);
        setError("");
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/admin/payments`,
                { ...getAuthConfig(), params: { page: currentPage, per_page: PAGE_SIZE } }
            );
            if (!response.data?.success) {
                throw new Error(response.data?.message || "Failed to load payments.");
            }

            const result = response.data?.data;
            setPayments(Array.isArray(result?.data) ? result.data : []);
            setPagination({
                currentPage: result?.current_page || currentPage,
                lastPage: result?.last_page || 1,
                total: result?.total || 0,
                from: result?.from || 0,
                to: result?.to || 0,
            });
        } catch (err) {
            console.error("Error fetching admin payments:", err);
            setError(err.response?.data?.message || err.message || "Error fetching payments.");
            setPayments([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPayments(page);
    }, [page, loadPayments]);

    const visiblePayments = useMemo(() => {
        const keyword = search.trim().toLowerCase();
        return payments.filter((payment) => {
            const textMatches = !keyword || [
                payment.id,
                payment.payable?.name,
                payment.payable?.email,
                payment.payable?.type,
                payment.payment_type,
                payment.status,
                payment.bank?.name,
                payment.creator?.name,
                payment.notes,
            ].some((value) => String(value || "").toLowerCase().includes(keyword));
            const paymentDate = payment.payment_date ? String(payment.payment_date).slice(0, 10) : "";
            return textMatches
                && (!statusFilter || payment.status === statusFilter)
                && (!typeFilter || payment.payment_type === typeFilter)
                && (!fromDate || paymentDate >= fromDate)
                && (!toDate || paymentDate <= toDate);
        });
    }, [payments, search, statusFilter, typeFilter, fromDate, toDate]);

    const resetFilters = () => {
        setSearch("");
        setStatusFilter("pending");
        setTypeFilter("");
        setFromDate("");
        setToDate("");
        setPage(1);
    };

    const showDetails = async (paymentId) => {
        setDetailLoading(true);
        setSelectedPayment(null);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/admin/payments/${paymentId}`,
                getAuthConfig()
            );
            if (!response.data?.success) {
                throw new Error(response.data?.message || "Failed to load payment details.");
            }
            setSelectedPayment(response.data?.data || null);
        } catch (err) {
            console.error("Error fetching payment details:", err);
            Swal.fire({
                icon: "error",
                title: t("Could not load payment"),
                text: err.response?.data?.message || err.message || t("Please try again."),
            });
        } finally {
            setDetailLoading(false);
        }
    };

    const updatePaymentStatus = async (payment, action) => {
        const approve = action === "approve";
        const result = await Swal.fire({
            icon: approve ? "question" : "warning",
            title: approve ? t("Approve this payment?") : t("Cancel this payment?"),
            text: approve
                ? t("This payment will be marked as approved.")
                : t("This payment will be marked as cancelled."),
            showCancelButton: true,
            confirmButtonText: approve ? t("Approve") : t("Cancel payment"),
            confirmButtonColor: approve ? "#198754" : "#dc3545",
            cancelButtonText: t("Go back"),
        });
        if (!result.isConfirmed) return;

        setActionId(payment.id);
        setError("");
        try {
            const response = await axios.put(
                `${process.env.REACT_APP_API_URL}/admin/payments/${payment.id}/${action}`,
                {},
                getAuthConfig()
            );
            if (!response.data?.success) {
                throw new Error(response.data?.message || `Failed to ${action} payment.`);
            }
            await Swal.fire({
                icon: "success",
                title: t("Payment updated"),
                text: response.data?.message || t("Payment status updated successfully."),
                timer: 1800,
                showConfirmButton: false,
            });
            await loadPayments(page);
            if (selectedPayment?.id === payment.id && response.data?.data) {
                setSelectedPayment(response.data.data);
            }
        } catch (err) {
            console.error(`Error ${action}ing payment:`, err);
            const message = err.response?.data?.message || err.message || `Unable to ${action} payment.`;
            setError(message);
            Swal.fire({
                icon: "error",
                title: t("Payment update failed"),
                text: message,
            });
        } finally {
            setActionId(null);
        }
    };

    const paymentDetails = selectedPayment;

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Body>
                            <h3 className="fw-bold mb-1">{t("Payments")}</h3>
                            <p className="text-muted mb-0">{t("Review payments and approve or cancel pending transactions.")}</p>
                        </Card.Body>
                    </Card>
                    {error && <Alert variant="danger">{error}</Alert>}

                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white py-3">
                            <Row className="g-3 align-items-end">
                                <Col md={6} lg={5}>
                                    <Form.Group>
                                        <Form.Label className="small fw-semibold text-muted">{t("Search this page")}</Form.Label>
                                        <Form.Control
                                            type="search"
                                            value={search}
                                            onChange={(event) => setSearch(event.target.value)}
                                            placeholder={t("Payee, email, bank, notes,")}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col sm={6} md={3}>
                                    <Form.Group>
                                        <Form.Label className="small fw-semibold text-muted">{t("Status")}</Form.Label>
                                        <Form.Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                                            <option value="">{t("All statuses")}</option>
                                            <option value="pending">{t("Pending")}</option>
                                            <option value="approved">{t("Approved")}</option>
                                            <option value="cancelled">{t("Cancelled")}</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col sm={6} md={3}>
                                    <Form.Group>
                                        <Form.Label className="small fw-semibold text-muted">{t("Payment type")}</Form.Label>
                                        <Form.Select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
                                            <option value="">{t("All types")}</option>
                                            <option value="credit">{t("Credit")}</option>
                                            <option value="debit">{t("Debit")}</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col sm={6} md={3}>
                                    <Form.Group>
                                        <Form.Label className="small fw-semibold text-muted">{t("From Date")}</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={fromDate}
                                            max={toDate || undefined}
                                            onChange={(event) => setFromDate(event.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col sm={6} md={3}>
                                    <Form.Group>
                                        <Form.Label className="small fw-semibold text-muted">{t("To Date")}</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={toDate}
                                            min={fromDate || undefined}
                                            onChange={(event) => setToDate(event.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col sm={6} md={3} className="d-flex align-items-end">
                                    <Button variant="secondary" className="w-100" onClick={resetFilters}>
                                        {t("Reset filters")}
                                    </Button>
                                </Col>
                                
                            </Row>
                        </Card.Header>
                        {loading ? (
                            <Card.Body className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <div className="small text-muted mt-2">{t("Loading payments...")}</div>
                            </Card.Body>
                        ) : (
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>{t("Payee")}</th>
                                            <th>{t("Type")}</th>
                                            <th>{t("Status")}</th>
                                            <th>{t("Payment Date")}</th>
                                            <th>{t("Bank")}</th>
                                            <th>{t("Recorded By")}</th>
                                            <th className="text-end">{t("Amount")}</th>
                                            <th className="text-end">{t("Actions")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visiblePayments.length ? visiblePayments.map((payment) => (
                                            <tr key={payment.id}>
                                                <td>
                                                    <div className="fw-semibold">{payment.payable?.name || "-"}</div>
                                                    <small className="text-muted">
                                                        {payment.payable?.type || "-"}
                                                        {payment.payable?.email ? ` · ${payment.payable.email}` : ""}
                                                    </small>
                                                </td>
                                                <td>
                                                    <Badge bg={payment.payment_type === "credit" ? "info" : "primary"}>
                                                        {t(payment.payment_type || "-")}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <Badge bg={getStatusVariant(payment.status)}>
                                                        {t(payment.status || "pending")}
                                                    </Badge>
                                                </td>
                                                <td>{formatDate(payment.payment_date)}</td>
                                                <td>{payment.bank?.name || "-"}</td>
                                                <td>{payment.creator?.name || "-"}</td>
                                                <td className="text-end fw-semibold">PKR {formatAmount(payment.amount)}</td>
                                                <td className="text-end text-nowrap">
                                                    <Button
                                                        variant="secondary"
                                                        size="sm"
                                                        className="me-1"
                                                        disabled={actionId === payment.id}
                                                        onClick={() => showDetails(payment.id)}
                                                    >
                                                        {t("View")}
                                                    </Button>
                                                    {payment.status === "pending" && (
                                                        <>
                                                            <Button
                                                                variant="success"
                                                                size="sm"
                                                                className="me-1"
                                                                disabled={actionId === payment.id}
                                                                onClick={() => updatePaymentStatus(payment, "approve")}
                                                            >
                                                                {actionId === payment.id ? <Spinner size="sm" animation="border" /> : t("Approve")}
                                                            </Button>
                                                            <Button
                                                                variant="danger"
                                                                size="sm"
                                                                disabled={actionId === payment.id}
                                                                onClick={() => updatePaymentStatus(payment, "cancel")}
                                                            >
                                                                {t("Cancel")}
                                                            </Button>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan={8} className="text-center text-muted py-5">
                                                    {t("No payments found on this page.")}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                        <PaymentPagination pagination={pagination} onPageChange={setPage} />
                    </Card>
                </Col>
            </Row>

            <Modal
                show={detailLoading || Boolean(paymentDetails)}
                onHide={() => setSelectedPayment(null)}
                centered
                size="lg"
                contentClassName="border-0 rounded-4 shadow"
            >
                <Modal.Header closeButton className="border-0 px-4 pt-4 pb-2">
                    <div>
                        <Modal.Title className="fw-bold">{t("Payment details")}</Modal.Title>
                        <div className="small text-muted mt-1">{t("Review the payment information below.")}</div>
                    </div>
                </Modal.Header>
                <Modal.Body className="px-4 pt-3 pb-4">
                    {detailLoading ? (
                        <div className="text-center py-4"><Spinner animation="border" /></div>
                    ) : paymentDetails && (
                        <>
                            <Card className="border-0 bg-light mb-3">
                                <Card.Body className="d-flex flex-wrap justify-content-between align-items-center gap-3 py-3 px-4">
                                    <div>
                                        <div className="small text-muted">{t("Payment amount")}</div>
                                        <div className="fs-3 fw-bold text-dark">
                                            PKR {formatAmount(paymentDetails.amount)}
                                        </div>
                                    </div>
                                    <div className="d-flex flex-wrap gap-2">
                                        <Badge bg={paymentDetails.payment_type === "credit" ? "info" : "primary"} className="px-3 py-2">
                                            {t(paymentDetails.payment_type || "-")}
                                        </Badge>
                                        <Badge bg={getStatusVariant(paymentDetails.status)} className="px-3 py-2">
                                            {t(paymentDetails.status || "pending")}
                                        </Badge>
                                    </div>
                                </Card.Body>
                            </Card>
                            <Row className="g-3">
                                {[
                                    [t("Payee"), paymentDetails.payable?.name || "-"],
                                    [t("Email"), paymentDetails.payable?.email || "-"],
                                    [t("Payee type"), paymentDetails.payable?.type || "-"],
                                    [t("Payment date"), formatDate(paymentDetails.payment_date)],
                                    [t("Bank"), paymentDetails.bank?.name || "-"],
                                    [t("Recorded by"), paymentDetails.creator?.name || "-"],
                                    [t("Notes"), paymentDetails.notes || "-"],
                                ].map(([label, value]) => (
                                    <Col xs={12} sm={label === t("Notes") ? 12 : 6} key={label}>
                                        <div className="h-100 rounded-3 border p-3">
                                            <div className="small text-muted mb-1">{label}</div>
                                            <div className="fw-semibold text-break">{value}</div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </>
                    )}
                </Modal.Body>
                {paymentDetails?.status === "pending" && (
                    <Modal.Footer className="border-0 px-4 pb-4 pt-0">
                        <Button
                            variant="danger"
                            className="px-4"
                            disabled={actionId === paymentDetails.id}
                            onClick={() => updatePaymentStatus(paymentDetails, "cancel")}
                        >
                            {t("Cancel payment")}
                        </Button>
                        <Button
                            variant="success"
                            className="px-4"
                            disabled={actionId === paymentDetails.id}
                            onClick={() => updatePaymentStatus(paymentDetails, "approve")}
                        >
                            {actionId === paymentDetails.id ? <Spinner size="sm" animation="border" /> : t("Approve payment")}
                        </Button>
                    </Modal.Footer>
                )}
            </Modal>
        </PageLayout>
    );
}
